import { lazy } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { organizationTabs as extensionTabs } from "@afriannotate/extensions";

const ALLOW_ORGANIZATION_WEBHOOKS = window.APP_SETTINGS.flags?.allow_organization_webhooks;

// Route-level lazy-loading of the organization (admin/manager) area.
// Annotators never open these pages, so their code should not sit in
// main.js on first paint. Each sub-page's ROUTE METADATA (path / exact /
// title) is declared EAGERLY here — copied verbatim from the page's own
// static props — while only the component is lazy-loaded via ``import()``.
//
// pageSetToRoutes (utils/routeHelpers) reads ``page.path`` / ``page.exact``
// / ``page.title`` from these descriptor objects synchronously; the lazy
// component is resolved under the <Suspense> boundary in ProjectRoutes.
//
// NOTE: MembersPage previously had its own ``.path = "/"`` mutated to
// "/members" (+ exact) here at module load. The descriptor below pins
// "/members" / exact directly, so the mutation is no longer needed.
const lazyOrg = (loader, name) => lazy(() => loader().then((m) => ({ default: m[name] })));

// The Resources sections moved under /organization/resources/; the old
// addresses (bookmarks, emails) land on the new ones.
const movedTo = (to) => {
  const Moved = () => {
    const { pathname, search, hash } = useLocation();
    const rest = pathname.replace(/^\/organization\/[^/]+/, "");
    return <Redirect to={`${to}${rest}${search}${hash}`} />;
  };
  return Moved;
};

const organizationPages = [
  {
    path: "/members",
    exact: true,
    title: "Members",
    component: lazyOrg(() => import("./MembersPage/MembersPage"), "MembersPage"),
  },
  {
    path: "/settings",
    exact: true,
    title: "Settings",
    component: lazyOrg(() => import("./OrgSettingsPage/OrgSettingsPage"), "OrgSettingsPage"),
  },
  {
    path: "/analytics",
    exact: true,
    title: "Analytics",
    component: lazyOrg(() => import("./OrgMetrics/OrgMetrics"), "OrgMetrics"),
  },
  {
    path: "/analytics/labels",
    exact: true,
    title: "Label Distribution",
    component: lazyOrg(() => import("./OrgLabelDistribution/OrgLabelDistribution"), "OrgLabelDistributionPage"),
  },
  {
    path: "/health",
    exact: true,
    title: "Health",
    component: lazyOrg(() => import("./OrgHealth/OrgHealthPage"), "OrgHealthPage"),
  },
  {
    path: "/activity",
    exact: true,
    title: "Activity",
    component: lazyOrg(() => import("./OrgActivity/OrgActivityPage"), "OrgActivityPage"),
  },
  {
    path: "/quality",
    exact: true,
    title: "Quality",
    component: lazyOrg(() => import("./OrgQuality/OrgQualityPage"), "OrgQualityPage"),
  },
  {
    path: "/review",
    exact: true,
    title: "Review",
    component: lazyOrg(() => import("./OrgReview/OrgReviewPage"), "OrgReviewPage"),
  },
  {
    path: "/payouts",
    exact: true,
    title: "Payouts",
    component: lazyOrg(() => import("./OrgPayouts/OrgPayoutsPage"), "OrgPayoutsPage"),
  },
  // ── Resources: every section under /organization/resources/ ──
  {
    path: "/resources/consent",
    exact: true,
    title: "Consent",
    component: lazyOrg(() => import("./OrgConsent/OrgConsentPage"), "OrgConsentPage"),
  },
  {
    path: "/resources/labels",
    exact: true,
    title: "Labels",
    component: lazyOrg(() => import("./OrgLabels/OrgLabelsPage"), "OrgLabelsPage"),
  },
  // Not exact: /resources/knowledge/<id> opens one knowledge base.
  {
    path: "/resources/knowledge",
    title: "Knowledge bases",
    component: lazyOrg(() => import("./OrgKnowledgePage"), "OrgKnowledgePage"),
  },
  { path: "/resources", exact: true, title: "Resources", component: movedTo("/organization/resources/templates") },
  {
    path: "/templates",
    exact: true,
    title: "Labeling templates",
    component: movedTo("/organization/resources/templates"),
  },
  { path: "/consent", exact: true, title: "Consent", component: movedTo("/organization/resources/consent") },
  { path: "/labels", exact: true, title: "Labels", component: movedTo("/organization/resources/labels") },
  { path: "/knowledge", title: "Knowledge bases", component: movedTo("/organization/resources/knowledge") },
  {
    path: "/policies",
    exact: true,
    title: "Policies",
    component: lazyOrg(() => import("./OrgPolicies/OrgPoliciesPage"), "OrgPoliciesPage"),
  },
  {
    path: "/workspaces",
    exact: true,
    title: "Workspaces",
    component: lazyOrg(() => import("../Workspaces/WorkspacesPage"), "WorkspacesPage"),
  },
  {
    path: "/resources/templates",
    exact: true,
    title: "Labeling templates",
    component: lazyOrg(() => import("../ProjectTemplates/ProjectTemplatesPage"), "OrgTemplatesPage"),
  },
];

if (ALLOW_ORGANIZATION_WEBHOOKS) {
  // WebhookPage is exported as an object descriptor ({ title, path, component }),
  // so the lazy default is its ``.component`` (the Webhook function), not the object.
  organizationPages.push({
    path: "/webhooks",
    title: "Webhooks",
    component: lazy(() => import("../WebhookPage/WebhookPage").then((m) => ({ default: m.WebhookPage.component }))),
  });
}

// Tabs an installed extension adds to the Organization page.
for (const tab of extensionTabs ?? []) {
  if (tab?.path && tab?.component) {
    organizationPages.push({ path: tab.path, exact: true, title: tab.label, component: tab.component });
  }
}
// The /organization/ root shows OrgOverviewPage — a dashboard with the
// org's name, stats, quick actions, and previews. Nested routes above
// mount the individual admin sub-pages.
export const OrganizationPage = {
  title: "Organization",
  path: "/organization",
  exact: true,
  component: lazyOrg(() => import("./OrgOverview/OrgOverviewPage"), "OrgOverviewPage"),
  routes: organizationPages,
};
